import { useMemo } from 'react';
import { AnalyticsCommonTypes, PracticeAnalyticsAggregations, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip } from '@frontend/analytics-shared';
import { AreaChartData, Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { trackingIds } from '../../tracking-ids';
import { CompareLocationsButton } from '../compare-locations-button';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { useMonthlyTrendChartModal } from './monthly-trend-chart-modal';
import { PracticeAnalyticsInfoTips } from './practice-analytics-info-tips';

type Props = PracticeAnalyticsTypes.MetricProps & {
  colors: AnalyticsCommonTypes.StringRecord;
  data?: ReturnType<typeof PracticeAnalyticsAggregations.recapturedPatientsSummary>;
  isLoading?: boolean;
  labels: AnalyticsCommonTypes.StringRecord;
};

export const RecapturedMetricSingle = ({ clickNoop, colors, data, isDrillDownPage, isLoading, labels }: Props) => {
  const { t } = useTranslation('analytics');
  const navigate = usePaNavigate();
  const { filters, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore('filters', 'showDemoChipAndBanner');

  const monthlyTrendModal = useMonthlyTrendChartModal({
    defaultKey: 'total',
    metric: 'recapturedPatients',
    tabs: { total: t('Patients'), production: t('Production') },
    title: t('Trend: Recaptured Patients'),
    trackingId: trackingIds.practiceAnalytics.recapturedPatientsTrend,
  });

  const multipleLocationsSelected = (filters.locations?.length || 0) > 1;

  const chartData = useMemo(() => {
    const { recapturedPatients } = data?.aggregatedData || {};

    const processedData = recapturedPatients?.historicalData.reduce(
      (acc, { label, leadTime = 0, production = 0, timestamp, total = 0 }) => {
        const x = timestamp ? formatters.date(timestamp, true) : label.replace('\\n', '-');

        return {
          ...acc,
          groups: [
            ...(acc.groups || []),
            {
              name: x,
              values: {
                leadTime,
                patients: total,
                production,
              },
            },
          ],
        };
      },
      {
        groups: [],
      } as AreaChartData
    );

    return {
      data: processedData,
      totalLeadTime: recapturedPatients?.totals?.leadTime || 0,
      totalRecaupturedPatients: recapturedPatients?.totals?.patients || 0,
      totalRecaupturedProduction: recapturedPatients?.totals?.production || 0,
    };
  }, [data]);

  return (
    <>
      <Chart
        colors={colors}
        isLoading={isLoading}
        labels={labels}
        onClick={
          clickNoop
            ? undefined
            : () => {
                navigate({
                  to: '/analytics/practice/recaptured-patients',
                });
              }
        }
        trackingId={trackingIds.practiceAnalytics.recapturedPatientsChart}
      >
        {isDrillDownPage ? (
          showDemoChipAndBanner && <DemoChip />
        ) : (
          <Chart.Header
            actions={[monthlyTrendModal.triggerProps]}
            bottomElement={
              multipleLocationsSelected ? <CompareLocationsButton style={{ marginBottom: theme.spacing(2) }} /> : null
            }
            infoTip={<PracticeAnalyticsInfoTips tip='recapturedPatients' />}
            leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
            title={t('Recaptured')}
          />
        )}
        <Chart.AreaChart
          appearance={{
            height: 180,
            showXAxis: true,
            showYAxis: true,
          }}
          data={chartData.data}
          formatValue={{
            leadTime: formatters.value,
            patients: formatters.value,
            production: formatters.currency,
          }}
          multiAxisClubIds={{
            leadTime: 'value',
            patients: 'value',
            production: 'production',
          }}
        />
        <Chart.Legends
          customData={{
            patients: {
              value: formatters.value(chartData?.totalRecaupturedPatients),
            },
            production: {
              value: formatters.currency(chartData?.totalRecaupturedProduction),
            },
            leadTime: {
              label: t('Avg. Lead Time (Days)'),
              value: formatters.value(Math.round(chartData?.totalLeadTime)),
            },
          }}
        />
      </Chart>
      {monthlyTrendModal.modalRenderer()}
    </>
  );
};
